import { httpClient } from '@/services/http/httpClient'
import type { QueryParams } from '@/utils/queryParams'
import { getQueryParams } from '@/utils/queryParams'

export type SimilarIdeaHttpResponse = {
  id: string
  title: string
  kanbanStep: string
  tennantId: string
  campaignId: string
  kanbanStepId: string
  score: number
  sequence: string
  likes: number
  description: string
  createdAt: string
  ideaUsers: {
    user: {
      id: string
      name: string
      email: string
      image?: string
      area?: {
        id: string
        name: string
      }
    }
  }[]
  campaign: {
    id: string
    sequence: number
    title: string
  }
  ideaStatuses: {
    name: string
    customName: string | null
    color: string
    customColor: string | null
  }
  ideaTags: {
    id: string
    tag: {
      id: string
      name: string
      color: string
      textColor: string
    }
  }[]
  ideaAITags: {
    aiTag: {
      name: string
    }
  }[]
  similarIdeasCount: number
  kanbanStepSequence: number
  isExcludable: boolean
  isEditable: boolean
  similarity: number
}

export type SimilarIdeasHttpResponse = {
  ideas: SimilarIdeaHttpResponse[]
  paginate: {
    page: number
    limit: number
    total: number
    pages: number
  }
}

export type SimilarIdea = {
  id: string
  sequence: string
  title: string
  description: string
  ideaStatuses: {
    name: string
    customName: string | null
    color: string
    customColor: string | null
  }
  likes: number
  participants: {
    id: string
    name: string
    email: string
    image?: string
    area?: {
      id: string
      name: string
    }
  }[]
  challenge: {
    id: string
    sequence: string
    title: string
  }
  ideaTags: {
    id: string
    tag: {
      id: string
      name: string
      color: string
      textColor: string
    }
  }[]
  ideaAITags: {
    aiTag: {
      name: string
    }
  }[]
  similarity: number
}

export type SimilarIdeasResponse = {
  ideas: SimilarIdea[]
  pagination: {
    page: number
    limit: number
    total: number
    totalPages: number
  }
}

type GetSimilarIdeasParams = {
  ideaId: string
  query: QueryParams
}

export async function getSimilarIdeas({
  ideaId,
  query,
}: GetSimilarIdeasParams): Promise<SimilarIdeasResponse> {
  const queryParams = getQueryParams(query)

  const { data } = await httpClient.get<SimilarIdeasHttpResponse>(
    `ideas/ai/similar-ideas/${ideaId}?${queryParams}`,
  )

  return {
    ideas: data.ideas.map((idea) => ({
      id: idea.id,
      sequence: idea.sequence,
      title: idea.title,
      description: idea.description,
      likes: idea.likes,
      ideaStatuses: idea.ideaStatuses
        ? {
            name: idea.ideaStatuses.name,
            customName: idea.ideaStatuses.customName,
            color: idea.ideaStatuses.color,
            customColor: idea.ideaStatuses.customColor,
          }
        : {
            name: 'Sem status',
            customName: null,
            color: '#000',
            customColor: null,
          },
      ideaTags: idea.ideaTags,
      ideaAITags: idea.ideaAITags,
      participants: idea.ideaUsers.map((ideaUser) => ({
        id: ideaUser.user.id,
        name: ideaUser.user.name,
        email: ideaUser.user.email,
        image: ideaUser.user.image,
        area: ideaUser.user.area,
      })),
      challenge: {
        id: idea.campaign.id,
        sequence: `#${String(idea.campaign.sequence).padStart(2, '0')}`,
        title: idea.campaign.title,
      },
      similarity: idea.similarity,
    })),
    pagination: {
      page: data.paginate.page,
      limit: data.paginate.limit,
      total: data.paginate.total,
      totalPages: data.paginate.pages,
    },
  }
}
